import { useState, type CSSProperties } from "react";
import type { DailyCanvas } from "./types";

type DailySidebarProps = {
  canvases: DailyCanvas[];
  activeCanvasId: string;
  onSelectCanvas: (canvasId: string) => void;
  onCreateCanvas: () => void;
  onRenameCanvas: (canvasId: string) => void;
  onDeleteCanvas: (canvasId: string) => void;
};

export function DailySidebar({
  canvases,
  activeCanvasId,
  onSelectCanvas,
  onCreateCanvas,
  onRenameCanvas,
  onDeleteCanvas,
}: DailySidebarProps) {
  const [openMenuCanvasId, setOpenMenuCanvasId] = useState<string | null>(null);

  return (
    <aside style={styles.sidebar}>
      <div style={styles.sidebarHeader}>
        <span style={styles.sidebarTitle}>Canvases</span>
        <button type="button" style={styles.createButton} onClick={onCreateCanvas}>
          +
        </button>
      </div>

      <div style={styles.canvasList}>
        {canvases.map((canvas) => {
          const active = canvas.id === activeCanvasId;
          const menuOpen = canvas.id === openMenuCanvasId;

          return (
            <div key={canvas.id} style={styles.canvasItemWrap}>
              <button
                type="button"
                style={{
                  ...styles.canvasItem,
                  ...(active ? styles.canvasItemActive : {}),
                }}
                onClick={() => onSelectCanvas(canvas.id)}
              >
                <span style={styles.canvasName}>{canvas.name}</span>
              </button>

              {active && (
                <button
                  type="button"
                  style={styles.menuButton}
                  onClick={() =>
                    setOpenMenuCanvasId(menuOpen ? null : canvas.id)
                  }
                  aria-label={`Open ${canvas.name} canvas menu`}
                >
                  ...
                </button>
              )}

              {active && menuOpen && (
                <div style={styles.menu}>
                  <button
                    type="button"
                    style={styles.menuItem}
                    onClick={() => {
                      setOpenMenuCanvasId(null);
                      onRenameCanvas(canvas.id);
                    }}
                  >
                    Rename
                  </button>
                  <button
                    type="button"
                    style={styles.menuItemDanger}
                    onClick={() => {
                      setOpenMenuCanvasId(null);
                      onDeleteCanvas(canvas.id);
                    }}
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </aside>
  );
}

const styles = {
  sidebar: {
    width: "220px",
    flex: "0 0 220px",
    borderRight: "1px solid var(--border)",
    background: "var(--bg)",
    padding: "16px 10px",
    boxSizing: "border-box",
  },
  sidebarHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    marginBottom: "12px",
  },
  sidebarTitle: {
    color: "var(--text-h)",
    fontSize: "14px",
    fontWeight: 600,
  },
  createButton: {
    width: "28px",
    height: "28px",
    border: "1px solid var(--border)",
    borderRadius: "6px",
    background: "var(--bg)",
    color: "var(--text-h)",
    cursor: "pointer",
  },
  canvasList: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  canvasItemWrap: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  canvasItem: {
    width: "100%",
    border: 0,
    borderRadius: "6px",
    background: "transparent",
    color: "var(--text)",
    padding: "8px 36px 8px 10px",
    textAlign: "left",
    cursor: "pointer",
  },
  canvasItemActive: {
    background: "var(--accent-bg)",
    color: "var(--text-h)",
  },
  canvasName: {
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  menuButton: {
    position: "absolute",
    right: "4px",
    width: "28px",
    height: "28px",
    border: 0,
    borderRadius: "6px",
    background: "transparent",
    color: "var(--text-h)",
    cursor: "pointer",
  },
  menu: {
    position: "absolute",
    top: "32px",
    right: "4px",
    zIndex: 20,
    minWidth: "112px",
    border: "1px solid var(--border)",
    borderRadius: "6px",
    background: "var(--bg)",
    boxShadow: "var(--shadow)",
    overflow: "hidden",
  },
  menuItem: {
    width: "100%",
    border: 0,
    background: "transparent",
    color: "var(--text-h)",
    padding: "8px 10px",
    textAlign: "left",
    cursor: "pointer",
  },
  menuItemDanger: {
    width: "100%",
    border: 0,
    background: "transparent",
    color: "#ef4444",
    padding: "8px 10px",
    textAlign: "left",
    cursor: "pointer",
  },
} satisfies Record<string, CSSProperties>;
